import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';

interface ErrorScreenProps {
  title: string;
  /** Short mono code for the kind of failure, e.g. "STALE BUILD". */
  code?: string;
  /** What went wrong, in plain language. */
  detail?: ReactNode;
  /** The one thing the user should do next. */
  action?: { label: string; busyLabel?: string; icon?: 'retry' | 'back'; onClick: () => void };
  /** A quiet way out that is not the primary recovery. */
  secondary?: { label: string; onClick: () => void };
}

const FRAMES = 9;
const GAP = 5;
const CELL = 28;
const STRIP_X = 34;
const SETTLE_MS = 640;

function timecode(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  const frame = Math.floor((date.getMilliseconds() / 1000) * 24);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}:${pad(frame)}`;
}

function reducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function DroppedFrameArt({ stoppedAt }: { stoppedAt: string }) {
  return (
    <svg className="error-art" viewBox="0 0 320 188" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id="error-art-picture">
          <rect x="52" y="28" width="216" height="98" rx="2" />
        </clipPath>
      </defs>

      <rect x="40" y="12" width="240" height="126" rx="6" className="error-art-surface" />
      <text x="52" y="23" className="error-art-mono">A001_C014</text>
      <g className="error-art-tc">
        <circle cx="194" cy="20" r="2.5" className="error-art-rec" />
        <text x="268" y="23" textAnchor="end" className="error-art-mono">{stoppedAt}</text>
      </g>

      <g clipPath="url(#error-art-picture)">
        <rect x="52" y="28" width="216" height="98" className="error-art-picture" />
        <g className="error-art-shot">
          <circle cx="206" cy="58" r="12" className="error-art-line" />
          <path d="M52 112 L104 70 L132 92 L170 58 L230 106 L268 84" className="error-art-line" />
          <path d="M52 126 L52 112 L104 70 L132 92 L170 58 L230 106 L268 84 L268 126 Z" className="error-art-ground" />
        </g>
        <g className="error-art-tear">
          <rect x="52" y="80" width="216" height="14" className="error-art-picture" />
          <path d="M52 94 L78 94 L104 70 M132 92 L150 80" className="error-art-line" />
          <rect x="52" y="80" width="216" height="1" className="error-art-accent-fill" />
          <rect x="52" y="93" width="216" height="1" className="error-art-accent-fill error-art-faint-fill" />
        </g>
        <rect x="52" y="28" width="216" height="10" className="error-art-scan" />
      </g>

      <path d="M58 34 h10 M58 34 v10 M262 34 h-10 M262 34 v10 M58 120 h10 M58 120 v-10 M262 120 h-10 M262 120 v-10" className="error-art-line error-art-crop" />

      <g className="error-art-badge">
        <rect x="112" y="64" width="96" height="22" rx="4" className="error-art-badge-plate" />
        <text x="160" y="78.5" textAnchor="middle" className="error-art-mono error-art-badge-text">
          FRAME DROPPED
        </text>
      </g>

      <g className="error-art-strip">
        {Array.from({ length: FRAMES + 1 }, (_, index) => (
          <g key={index}>
            <rect x={STRIP_X + index * CELL - 8} y="150" width="6" height="3" rx="1" className="error-art-sprocket" />
            <rect x={STRIP_X + index * CELL - 8} y="181" width="6" height="3" rx="1" className="error-art-sprocket" />
          </g>
        ))}
        {Array.from({ length: FRAMES }, (_, index) =>
          index === GAP ? (
            <g key={index} className="error-art-gap">
              <rect x={STRIP_X + index * CELL} y="157" width="22" height="20" rx="2" className="error-art-gap-outline" />
              <rect x={STRIP_X + index * CELL} y="157" width="22" height="20" rx="2" className="error-art-gap-fill" />
            </g>
          ) : (
            <g key={index}>
              <rect x={STRIP_X + index * CELL} y="157" width="22" height="20" rx="2" className="error-art-frame" />
              <path d={`M${STRIP_X + index * CELL + 3} 173 l6 -7 l4 4 l6 -6`} className="error-art-line error-art-faint" />
            </g>
          )
        )}
      </g>

      <g className="error-art-playhead">
        <path d="M0 146 l-4 -6 h8 z" className="error-art-accent-fill" />
        <rect x="-0.75" y="146" width="1.5" height="40" className="error-art-accent-fill" />
      </g>
    </svg>
  );
}

/**
 * H6: a failure is an artifact the user acts on. The dropped-frame viewer
 * says where playback stopped; the single primary control is the recovery.
 */
export default function ErrorScreen({ title, code, detail, action, secondary }: ErrorScreenProps) {
  const [stoppedAt] = useState(() => new Date());
  const [busy, setBusy] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), []);

  const run = () => {
    if (!action || busy) return;
    setBusy(true);
    if (reducedMotion()) {
      action.onClick();
      return;
    }
    timers.current.push(window.setTimeout(action.onClick, SETTLE_MS));
    timers.current.push(window.setTimeout(() => setBusy(false), 8000));
  };

  const scrub = (event: PointerEvent<HTMLDivElement>) => {
    const target = stage.current;
    if (!target || event.pointerType === 'touch' || busy) return;
    const box = (target.querySelector('svg') || target).getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    target.style.setProperty('--scrub', ratio.toFixed(3));
    target.classList.add('is-scrubbing');
    target.classList.toggle('is-on-gap', Math.floor((ratio * 320 - STRIP_X) / CELL) === GAP);
  };

  const release = () => {
    stage.current?.classList.remove('is-scrubbing', 'is-on-gap');
  };

  const clock = stoppedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <main className="auth-screen error-screen-shell">
      <section className="auth-card error-screen" data-state={busy ? 'retrying' : 'halted'} role="alert" aria-busy={busy}>
        <a className="error-screen-brand" href="/">
          <img src="/brand/frameon-mark.svg" alt="" width="28" height="28" />
          <span>Frameon</span>
        </a>

        <div ref={stage} className="error-screen-stage" onPointerMove={scrub} onPointerLeave={release}>
          <DroppedFrameArt stoppedAt={timecode(stoppedAt)} />
        </div>

        <p className="error-screen-eyebrow">
          <span className="error-screen-dot" aria-hidden="true" />
          {busy ? 'Reconnecting' : 'Playback halted'}
          {code ? <span className="error-screen-code">{code}</span> : null}
        </p>
        <h1>{title}</h1>
        {detail ? <p className="error-screen-detail">{detail}</p> : null}

        {action ? (
          <div className="error-screen-actions">
            <button className="app-button primary large error-screen-retry" type="button" onClick={run} disabled={busy} autoFocus>
              <svg className={`error-screen-retry-icon is-${action.icon || 'retry'}`} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                {action.icon === 'back' ? (
                  <path d="M13 8H3m4.5-4.5L3 8l4.5 4.5" />
                ) : (
                  <>
                    <path d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89" />
                    <path d="M13.5 2.5v3h-3" />
                  </>
                )}
              </svg>
              <span>{busy ? action.busyLabel || 'Reloading…' : action.label}</span>
              <i className="error-screen-retry-bar" aria-hidden="true" />
            </button>
            <span className="error-screen-meta">
              Stopped <time dateTime={stoppedAt.toISOString()}>{clock}</time>
            </span>
          </div>
        ) : null}

        {secondary ? (
          <button className="error-screen-link" type="button" onClick={secondary.onClick}>
            {secondary.label}
          </button>
        ) : null}

      </section>
    </main>
  );
}
